import type { CSSProperties } from 'react';
import { EMAIL_BUILDER_COLOR_BLUE_600 } from '../../schema/emailBuilderDesignColors';
import { DEFAULT_FONT } from "../create-shared";
import type {
  ButtonChrome,
  ButtonSize,
  ButtonSizing,
  CornerRadius,
  EmailFontFamily,
} from "../types";
import { radiusCss } from "../../render/table";

export type { ButtonSize } from "../types";

export const BUTTON_SIZES: readonly ButtonSize[] = ['sm', 'md', 'lg'];

export const DEFAULT_BUTTON_SIZE: ButtonSize = 'md';

export const BUTTON_SIZE_PADDING: Record<ButtonSize, { x: number; y: number }> = {
  sm: { x: 12, y: 8 },
  md: { x: 16, y: 12 },
  lg: { x: 24, y: 16 },
};

export const BUTTON_SIZE_FONT_SIZE: Record<ButtonSize, number> = {
  sm: 12,
  md: 14,
  lg: 16,
};

export const BUTTON_PADDING_X = BUTTON_SIZE_PADDING.md.x;
export const BUTTON_PADDING_Y = BUTTON_SIZE_PADDING.md.y;

export const BUTTON_PRIMARY_BG = EMAIL_BUILDER_COLOR_BLUE_600;
export const BUTTON_PRIMARY_COLOR = '#ffffff';
export const BUTTON_FONT_SIZE = BUTTON_SIZE_FONT_SIZE.md;
export const BUTTON_FONT_WEIGHT = 700;
export const BUTTON_LINE_HEIGHT = 20 / BUTTON_FONT_SIZE;
export const BUTTON_BORDER_RADIUS: CornerRadius = {
  topLeft: 8,
  topRight: 8,
  bottomRight: 8,
  bottomLeft: 8,
};

export function resolveButtonSizePadding(size: ButtonSize | undefined): { x: number; y: number } {
  return BUTTON_SIZE_PADDING[size ?? DEFAULT_BUTTON_SIZE];
}

export function resolveButtonSizeFontSize(size: ButtonSize | undefined): number {
  return BUTTON_SIZE_FONT_SIZE[size ?? DEFAULT_BUTTON_SIZE];
}

export function buttonSizePatch(size: ButtonSize): Pick<ButtonSizing, 'size' | 'fontSize'> {
  return {
    size,
    fontSize: resolveButtonSizeFontSize(size),
  };
}

export function buttonLabelPaddingCss(size: ButtonSize | undefined): string {
  const { x, y } = resolveButtonSizePadding(size);
  return `${y}px ${x}px`;
}

export function createDefaultButtonChrome(): ButtonChrome {
  return {
    borderWidth: 0,
    borderRadius: { ...BUTTON_BORDER_RADIUS },
  };
}

export function buttonChromeCellStyle(chrome: ButtonChrome): CSSProperties {
  const hasBorder = chrome.borderWidth > 0;
  const backgroundColor = chrome.backgroundColor ?? BUTTON_PRIMARY_BG;
  const borderColor = chrome.borderColor ?? backgroundColor;
  return {
    backgroundColor,
    borderRadius: radiusCss(chrome.borderRadius),
    textAlign: 'center',
    ...(hasBorder
      ? {
          boxShadow: `inset 0 0 0 ${chrome.borderWidth}px ${borderColor}`,
        }
      : {}),
  };
}

type ButtonLinkChrome = ButtonChrome & Partial<ButtonSizing>;

export function buttonChromeLinkStyle(
  chrome: ButtonLinkChrome,
  bodyFontFamily: EmailFontFamily = DEFAULT_FONT,
): CSSProperties {
  const { x: paddingX, y: paddingY } = resolveButtonSizePadding(chrome.size);
  return {
    display: 'inline-block',
    padding: `${paddingY}px ${paddingX}px`,
    color: chrome.color ?? BUTTON_PRIMARY_COLOR,
    fontSize: resolveButtonSizeFontSize(chrome.size),
    fontWeight: chrome.fontWeight ?? BUTTON_FONT_WEIGHT,
    fontFamily: bodyFontFamily,
    lineHeight: BUTTON_LINE_HEIGHT,
    letterSpacing: 'normal',
    textDecoration: 'none',
    borderRadius: radiusCss(chrome.borderRadius),
    whiteSpace: 'nowrap',
  };
}
